import React from 'react';
import { supabase } from '../supabase';
import { useUser } from '../context/UserContext';
import { pick, renameText } from '../i18n';

// Change the name shown on the leaderboard and in comments.
//
// Renaming is deliberate rather than a side effect of signing in with a
// shortened name — otherwise a merged account would quietly revert to the stub
// name someone typed once on a phone.
//
// The old name is kept as an alias, so signing in with it still works, and the
// reading history follows the account rather than the label.


export default function RenameAccount({ lang = 'en' }) {
  const { user, login } = useUser();
  const t = pick(renameText, lang);
  const [value, setValue] = React.useState(user?.name || '');
  const [state, setState] = React.useState('idle');   // idle | saving | saved | error
  const [message, setMessage] = React.useState('');

  React.useEffect(() => { setValue(user?.name || ''); }, [user?.name]);

  async function save() {
    const next = value.trim();
    if (!next) { setState('error'); setMessage(t.empty); return; }
    if (!user?.id || next === user.name) return;

    setState('saving'); setMessage('');

    // The server checks the name is free — an alias belonging to someone else
    // counts as taken too, otherwise their old name would stop reaching them —
    // keeps the old name as an alias so it still works at sign-in, and drops
    // any alias that matches the new name. It needs the account's email as well
    // as its id, so nobody can rename an account that is not theirs.
    const { data: result, error } = await supabase.rpc('rename_account', {
      p_id: user.id, p_email: user.email, p_new_name: next,
    });
    if (result === 'taken') { setState('error'); setMessage(t.taken); return; }
    if (error || result !== 'ok') { setState('error'); setMessage(t.failed); return; }

    login({ ...user, name: next });
    setState('saved'); setMessage(t.saved);
    setTimeout(() => setState('idle'), 2200);
  }

  if (!user?.id) return null;
  const dirty = value.trim() && value.trim() !== user.name;

  return (
    <div className="np-rename">
      <div className="np-heading">{t.heading}</div>
      <div className="np-rename-row">
        <input
          className="np-rename-input"
          value={value}
          onChange={e => { setValue(e.target.value); setState('idle'); setMessage(''); }}
          onKeyDown={e => { if (e.key === 'Enter' && dirty) save(); }}
          maxLength={40}
          aria-label={t.heading}
        />
        <button
          className="np-rename-save"
          onClick={save}
          disabled={!dirty || state === 'saving'}
        >
          {state === 'saving' ? t.saving : t.save}
        </button>
      </div>
      {message && (
        <div className={state === 'error' ? 'np-warn' : 'np-note'}>{message}</div>
      )}
      {dirty && state === 'idle' && <div className="np-note">{t.note}</div>}
    </div>
  );
}
